<script>
  import { allGames, availableGameTypes, gameDescriptions } from '@/data/games';
  import { GameLauncher } from '@/mixins/game-launcher';
  import GameCard from '@/components/cards/GameCard';

  const gameInfos = {
    maxblast: {
      PVP: [
        {
          text: 'Blast into space with up to 50 players',
          icon: 'RocketIcon',
        },
        {
          text: 'Be the last astronaut to eject before rocket the crashes',
          icon: 'DefeatIcon',
        },
        {
          text: 'Ensure the win to take home the entire prize pot',
          icon: 'CheckmarkIcon',
        },
      ],
      FP: [
        {
          text: 'Control up to 3 astronauts through space in one round',
          icon: 'RocketIcon',
        },
        {
          text: 'Eject before the rocket collides with an asteroid',
          icon: 'DefeatIcon',
        },
        {
          text: 'Win up to 100x your bid by successfully jumping out',
          icon: 'CheckmarkIcon',
        },
      ],
    },
    'sector-x': {
      PVP: [
        {
          text: 'Aim for the capsules to unlock special drops',
          icon: 'AlienIcon',
        },
        {
          text: 'Target the mega enemies to win up to 300x your bid',
          icon: 'DefeatIcon',
        },
        {
          text: 'Defeat one of the four bosses to increase your score',
          icon: 'CheckmarkIcon',
        },
      ],
      FP: [
        {
          text: 'Aim for the capsules for increased prizes',
          icon: 'AlienIcon',
        },
        {
          text: 'Target the mega enemies to win up to 300x your bid',
          icon: 'DefeatIcon',
        },
        {
          text: 'Defeat one of the four bosses and earn big rewards',
          icon: 'CheckmarkIcon',
        },
      ],
    },
    plinko: {
      PVP: [
        {
          text: 'Target the multipliers to achieve the highest score',
          icon: 'CometIcon',
        },
        {
          text: 'Use the bump feature to change the balls direction',
          icon: 'DefeatIcon',
        },
        {
          text: 'Win the round and take home the entire prize pot',
          icon: 'CheckmarkIcon',
        },
      ],
      FP: [
        {
          text: 'Launch up to 100 balls aiming for the multipliers',
          icon: 'CometIcon',
        },
        {
          text: 'Activate Bonus and Jackpot Wheels for increased prizes',
          icon: 'DefeatIcon',
        },
        {
          text: 'Use the turbocharge feature to speed up rounds by 30%',
          icon: 'CheckmarkIcon',
        },
      ],
    },
    'multi-mines': {
      PVP: [
        {
          text: 'Select squares in five rounds and find hidden gems',
          icon: 'DiamondIcon',
        },
        {
          text: 'Focus on the multipliers and stay clear of the mines',
          icon: 'DefeatIcon',
        },
        {
          text: 'Achieve the highest score to win the entire prize pot',
          icon: 'CheckmarkIcon',
        },
      ],
      FP: [
        {
          text: 'Click tiles to reveal Gems or Mines',
          icon: 'DiamondIcon',
        },
        {
          text: 'Gems increase payout, uncovering mine ends the game',
          icon: 'DefeatIcon',
        },
        {
          text: 'Gem multiplier increases with the number of mines',
          icon: 'CheckmarkIcon',
        },
      ],
    },
    dragonstone: {
      PVP: [
        {
          text: 'Collect 8 DragonStone fragments to summon the Dragon',
          icon: 'DragonIcon',
        },
        {
          text: 'Use special weapons for maximum damage',
          icon: 'DefeatIcon',
        },
        {
          text: 'Defeat the Dragon and win huge rewards',
          icon: 'CheckmarkIcon',
        },
      ],
      FP: [
        {
          text: 'Collect 8 DragonStone fragments to summon the Dragon',
          icon: 'DragonIcon',
        },
        {
          text: 'Target the Lightning Specter for increased rewards',
          icon: 'DefeatIcon',
        },
        {
          text: 'Defeat the Dragon and win up to 250x your bid',
          icon: 'CheckmarkIcon',
        },
      ],
    },
    'mission-amazon': {
      PVP: [
        {
          text: 'Collect rare gems to increase your score',
          icon: 'MonkeyIcon',
        },
        {
          text: 'Obliterate weapon carries to unlock special weapons',
          icon: 'DefeatIcon',
        },
        {
          text: 'Defeat the legendary bosses and win big rewards',
          icon: 'CheckmarkIcon',
        },
      ],
      FP: [
        {
          text: 'Collect rare gems and win up to 70x your bid',
          icon: 'MonkeyIcon',
        },
        {
          text: 'Use special weapons to defeat up to 26 enemies',
          icon: 'DefeatIcon',
        },
        {
          text: 'Defeat the legendary bosses and win up to 100x your bid',
          icon: 'CheckmarkIcon',
        },
      ],
    },
  };

  export default {
    name: 'GameLobbyComponent',

    components: {
      GameCard,
      RocketIcon: async () => await import('@/assets/images/svg/icons/rocket.svg'),
      DefeatIcon: async () => await import('@/assets/images/svg/icons/defeat.svg'),
      CheckmarkIcon: async () => await import('@/assets/images/svg/icons/checkmark.svg'),
      AlienIcon: async () => await import('@/assets/images/svg/icons/alien.svg'),
      CometIcon: async () => await import('@/assets/images/svg/icons/comet.svg'),
      DiamondIcon: async () => await import('@/assets/images/svg/icons/icon-diamond.svg'),
      DragonIcon: async () => await import('@/assets/images/svg/icons/dragon.svg'),
      MonkeyIcon: async () => await import('@/assets/images/svg/icons/monkey.svg'),
    },

    mixins: [GameLauncher],

    props: {
      gameType: {
        type: String,
        required: true,
        validator: (value) => availableGameTypes.has(value),
      },
    },

    computed: {
      assetsPath() {
        return '/images/game-lobby';
      },

      isDesktopLayout() {
        return this.$vuetify.breakpoint.width >= 744;
      },

      logoUrl() {
        return `${this.assetsPath}/logos/${this.gameType}-lg.webp?v=2`;
      },

      /**
       * Returns a filtered array of game cards that belong to the selected game type.
       *
       * @return {{title: String, image: String, category: String, gameType: String}[]} An array of game cards
       */
      gameCards() {
        return allGames
          .filter((game) => !game.hidden && game.gameType === this.gameType && game.category === 'PVP')
          .sort((a, b) => {
            if (a.category === 'PVP') return -1;
            if (b.category === 'PVP') return 1;
            return 0;
          });
      },

      description() {
        return gameDescriptions[this.gameType] || '';
      },

      bgUrl() {
        return `${this.assetsPath}/backgrounds/${this.gameType}-${this.isDesktopLayout ? 'lg' : 'sm'}.webp?v=1`;
      },

      gameInfo() {
        return gameInfos[this.gameType];
      },
    },

    methods: {
      onGameClick(game) {
        if (game.category === 'PVP') {
          this.$store.commit('SET_ROOM_BUYIN_POPUP_DATA', this.gameType);
        } else {
          this.handleGameClick(game);
        }
      },
    },
  };
</script>

<template>
  <div class="game-lobby">
    <div class="wrapper">
      <div class="background-image">
        <div class="content">
          <v-img
            :alt="`${gameType} lobby background`"
            :src="bgUrl"
            class="page-header"
            cover
            height="auto"
            width="100%"
          >
            <img :alt="`${gameType} logo`" :class="`game-logo--${gameType}`" :src="logoUrl" class="game-logo" />
            <div v-if="description" class="game-desc">
              {{ description }}
            </div>
          </v-img>

          <div class="game-list">
            <div v-for="(game, index) in gameCards" :key="`${game.category}-${index}`" class="card-container">
              <GameCard no-info show-ribbon v-bind="game" @item-clicked="onGameClick(game)" />
              <div class="game-info">
                <div>
                  <div v-for="(bullet, index) of gameInfo[game.category]" :key="index" class="info-bullet">
                    <component :is="bullet.icon" class="info-icon" />
                    <span v-html="bullet.text" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
  @import 'src/scss/mixins';

  .game-lobby {
    container: game-lobby / inline-size;
    position: relative;

    .wrapper {
      height: max-content;
    }

    .background-image {
      @include basic-spacings;
      padding-inline: 12px;
      max-width: 960px;
      margin-inline: auto;

      @media screen and (min-width: 420px) {
        padding-inline: $padding-small;
      }
    }

    .page-header {
      height: auto;
    }

    .content {
      border-radius: 10px;
      background: rgba(28, 6, 48, 0.6);
      position: relative;
      padding: 24px 0 60px 0;
    }

    .background-image {
      height: 100%;
    }
  }

  .game-logo {
    position: relative;
    display: block;
    margin: 0 auto;
    height: 100%;
  }

  @container game-lobby (width >= 0px) {
    .page-header {
      border-radius: 10px;
      padding-top: 10px;
      padding-bottom: 10px;
      @include respond(mobile-wide) {
        aspect-ratio: 49 / 25;
      }

      &::v-deep .v-responsive__content {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 8px;
      }
    }

    .game-logo {
      margin-bottom: 8px;
      aspect-ratio: 5 / 3;
      max-height: 100px;
    }

    .game-list {
      display: grid;
      grid-template-columns: 1fr;
      justify-items: center;
      justify-content: center;
      margin-top: 16px;
      gap: 24px;

      .card-container {
        max-width: 540px;
        width: 100%;
        height: 100%;
      }

      .game-card {
        max-width: none;
      }

      .game-info {
        container: game-card-info / inline-size;
        display: flex;
        padding: 10px;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 12px;
        border-radius: 10px;
        border: 1px solid rgba(255, 255, 255, 0.3);
        background: rgba(0, 0, 0, 0.4);
        color: #fff;
        margin-top: 8px;

        .info-bullet {
          display: flex;
          align-items: center;
          gap: 6px;

          span {
            font-size: 3.5cqi;
            @include respond(mobile-wide-down) {
              font-size: 4cqi;
            }
          }
        }

        .info-icon path {
          fill: #f8bf1e;
        }

        svg {
          width: 3cqi;
          height: 3cqi;
        }
      }
    }

    .game-desc {
      color: #fff;
      text-align: center;
      font-size: 16px;
      font-weight: 400;
      line-height: 120%;
      margin-inline: 14px;
    }

    ::v-deep .game-card-cta {
      width: calc(100% - 40px);
      max-width: none;
      bottom: 20px;
      aspect-ratio: 329/50;
      border-radius: 10px;

      .game-card-cta-text {
        color: #1c0630;
        font-family: 'Barlow', sans-serif;
        font-size: 18px;
        font-weight: 900;
        line-height: 100%;
      }
    }
  }

  @container game-lobby (width >= 744px) {
    .game-lobby .content {
      padding-top: 24px;
      height: auto;
    }

    .page-header {
      aspect-ratio: 16 / 3;

      &::v-deep .v-responsive__content {
        flex-direction: row;
      }
    }

    .game-logo {
      aspect-ratio: 5 / 3;
      max-width: 300px;
      max-height: none;
      margin: 0;
    }

    .game-desc {
      max-width: 600px;
      font-size: 20px;
      text-align: left;
    }

    .game-list {
      grid-template-columns: repeat(auto-fit, minmax(344px, 1fr));
      gap: 16px;
    }

    ::v-deep .game-card-cta {
      width: calc(100% - 16px);
      bottom: 8px;
    }
  }

  @container game-lobby (width >= 1200px) {
    .game-lobby {
      .background-image {
        padding-inline: 0;
      }
    }

    .game-list .game-info {
      padding: 16px;

      .info-bullet {
        gap: 10px;

        span {
          font-size: 16px;
        }
      }
    }

    ::v-deep .game-card-cta {
      width: calc(100% - 40px);
      bottom: 20px;
      aspect-ratio: 432/50;
    }
  }
</style>
